import { useCallback, useEffect, useMemo, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentTaxesService } from "../services/paymentTaxes.service";
import type { PaymentTax, TaxSummary } from "../types/paymentTaxes.types";
import { summarizeTaxes } from "../utils/taxMath";

export interface UsePaymentTaxesResult {
  rows: PaymentTax[];
  /** Country (ISO3) → its taxes, each with its general scopes and the organizations' own. */
  byCountry: Map<string, TaxSummary[]>;
  now: number;
  truncated: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  /** A 401/403 latches for the session: the whole surface becomes a no-access view. */
  hasAccess: boolean;
  refresh: () => void;
}

/** Every tax version, loaded whole so the on-screen resolver reads what the backend reads. No polling. */
export function usePaymentTaxes(): UsePaymentTaxesResult {
  const [rows, setRows] = useState<PaymentTax[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [settledKey, setSettledKey] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const isLoading = settledKey !== reloadKey && !isRefreshing;

  useEffect(() => {
    let cancelled = false;
    PaymentTaxesService.listAll()
      .then((response) => {
        if (cancelled) return;
        setRows(response.rows);
        setTruncated(response.truncated);
        setNow(Date.now());
        setHasError(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(error)) setHasAccess(false);
        else setHasError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setSettledKey(reloadKey);
        setIsRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    setReloadKey((key) => key + 1);
  }, []);

  const byCountry = useMemo(() => summarizeTaxes(rows, now), [rows, now]);

  return { rows, byCountry, now, truncated, isLoading, isRefreshing, hasError, hasAccess, refresh };
}
